import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import type { ShapeTextBakeContext } from '@/lib/elements/stowed-element-builder'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import type { TextChain } from '@/api/types/stowed-element'
import {
  textFramesAffectedByWrapSourceChange,
  wrapTargetTextFrames,
} from '@/lib/text/wrap-affected-text-frames'
import { buildStowedVisibleRectOptions } from '@/lib/elements/stowed-element-builder'
import type { HomePersistence } from '../types/home-groups'
import { buildStowedShapeElement } from '../helpers/build-stowed-shape-element'

export interface WrapAffectedTextFrameSaveDeps extends Pick<
  HomePersistence,
  'enqueueUpdate' | 'markEdited'
> {
  shapes: readonly ShapeData[]
  zIndexOf: (id: string) => number | undefined
  textChains: readonly TextChain[]
  resolveBake: (shape: ShapeData) => ShapeTextBakeContext
  projection?: DocumentProjection
  fonts?: ParagraphBlockFontLookup
}

/**
 * Re-saves every unlinked text frame a wrap-source change wrapped (or un-wrapped). yb360 paints a
 * frame's baked `<TL>`/`<TR>` lines verbatim, and those are only re-baked when the frame itself is
 * saved — so without this, wrapping a triangle over a text box wrapped the text in Larry while
 * yb360 kept the old lines. Linked frames are excluded: `redistributeChainStory` re-saves them.
 *
 * The frame's own data is unchanged, so this only enqueues the write; there is nothing to undo.
 * Shared by the forward commit handlers and the undo/redo step, which reverts the obstacle without
 * going through either of them.
 */
export function enqueueWrapAffectedTextFrameSaves(
  commits: readonly { before: ShapeData; after: ShapeData }[],
  historyTxnId: string | undefined,
  alreadySavedIds: ReadonlySet<string>,
  deps: WrapAffectedTextFrameSaveDeps
): void {
  if (commits.length === 0) return

  // Built once for the whole commit, so a batch checks each changed shape against these few frames
  // only, instead of rebuilding the linked-frame set and scanning every shape for each change.
  const candidates = wrapTargetTextFrames(deps.shapes, deps.textChains).filter(
    (frame) => !alreadySavedIds.has(frame.id)
  )
  const frames = new Map<string, ShapeData>()

  for (const { before, after } of commits) {
    if (frames.size === candidates.length) break

    for (const frame of textFramesAffectedByWrapSourceChange(
      before,
      after,
      candidates,
      deps.zIndexOf
    )) {
      frames.set(frame.id, frame)
    }
  }

  if (frames.size === 0) return

  void deps.markEdited([...frames.keys()])

  for (const frame of frames.values()) {
    deps.enqueueUpdate(
      buildStowedShapeElement(
        deps.resolveBake,
        frame,
        buildStowedVisibleRectOptions(frame.rotation, 'update'),
        deps.projection,
        deps.fonts
      ),
      {
        operation: 'update',
        meta: historyTxnId ? { historyTxnId } : undefined,
      }
    )
  }
}
